import React, { useState } from "react";
import styled from "@emotion/styled";
import CopyToClipboard from "react-copy-to-clipboard";
import { Element as ScrollElement } from "react-scroll";
import { NUMBER_OF_REFERRAL_TREES, User } from "@ecologi/shared";
import { Typography } from "../Typography";
import { Wrapper as InnerWrapper } from "../Wrapper";
import { TuftyButton } from "../Button/TuftyButton";

const OuterWrapper = styled(ScrollElement)`
  background-color: ${(props) => props.theme.colors.spruce};
  padding-top: 90px;
  padding-bottom: 90px;
`;

const Title = styled(Typography)`
  color: ${(props) => props.theme.colors.mushroomLight};
  text-align: center;
  margin-bottom: 20px;
  word-wrap: break-word;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: 20px;
`;

const BodyCopy = styled(Typography)`
  color: ${(props) => props.theme.colors.mushroomLight};
  text-align: center;
  margin-bottom: 20px;
`;

export type ContentProps = {
  title: string;
  body: string;
};

export type ReferralBannerProps = {
  content: ContentProps;
  user: User;
  referralId: string;
  isGuest?: boolean;
};

export const ReferralBanner = ({
  content,
  user,
  referralId,
  isGuest,
}: ReferralBannerProps) => {
  const [isCopied, setCopied] = useState(false);
  // we need origin in this link because it's a link that's getting copied/shared
  const referralLink = `${
    process.env.NEXT_PUBLIC_CLIENT_URL
  }${user.getProfilePath()}?direct=true&r=${referralId}`;

  return (
    <OuterWrapper name="share">
      <InnerWrapper>
        <Title use="sectionTitle" as="h4">
          {content.title}
        </Title>
        <Content>
          <BodyCopy use="body1" as="p">
            {content.body}
          </BodyCopy>
          {isGuest ? (
            <TuftyButton
              text="Join today"
              primary
              href={`/plan?direct=true&r=${referralId}`}
            />
          ) : (
            <CopyToClipboard
              text={referralLink}
              onCopy={() => {
                setCopied(true);
              }}
            >
              <div>
                <TuftyButton
                  text={isCopied ? "Link copied" : "Copy special link"}
                  isCopied={isCopied}
                  primary
                />
              </div>
            </CopyToClipboard>
          )}
        </Content>
      </InnerWrapper>
    </OuterWrapper>
  );
};

const loggedInContent = {
  body: `Together we are stronger, that’s why we believe in collective action. Share this special link, and if someone you know signs up, we’ll plant ${NUMBER_OF_REFERRAL_TREES} trees in your forest as a thank you!`,
};

export const businessContent = {
  ...loggedInContent,
  title: "Invite your network",
};

const Business = (props: Omit<ReferralBannerProps, "content">) => {
  return <ReferralBanner {...props} content={businessContent} />;
};

export const consumerContent = {
  ...loggedInContent,
  title: "Invite your buddies",
};

const Consumer = (props: Omit<ReferralBannerProps, "content">) => {
  return <ReferralBanner {...props} content={consumerContent} />;
};

export const peerContent = (name: string) => ({
  ...loggedInContent,
  title: `Share ${name} profile`,
});

const Peer = (props: Omit<ReferralBannerProps, "content">) => {
  return (
    <ReferralBanner
      {...props}
      content={peerContent(props.user.fullNamePlural)}
    />
  );
};

export const guestContent = {
  title: "Join the movement",
  body: `Together we are stronger, that’s why we believe in collective action. If you join us today (using the button below) you and the owner of this profile will be awarded ${NUMBER_OF_REFERRAL_TREES} extra trees!`,
};

const Guest = (props: Omit<ReferralBannerProps, "content">) => {
  return <ReferralBanner {...props} content={guestContent} isGuest />;
};

ReferralBanner.Business = Business;
ReferralBanner.Consumer = Consumer;
ReferralBanner.Guest = Guest;
ReferralBanner.Peer = Peer;
